import { EntityKind, type EdgeViewRecord, type SnapshotRecord } from "odyssey-shared";
import type { Welcome } from "./connection.ts";
import { emptyClientView, type EdgeNearFrame } from "./handoff-wire.ts";
import { EdgeFigures } from "./neighbours/edge-figures.ts";
import type { OwnName } from "./neighbours/edge-book.ts";
import type { RemoteCharacters } from "./remote-characters.ts";
import { gameReport } from "./report.ts";
import type { SessionParts, SessionWorld } from "./session-types.ts";
import type { CharacterVisitor } from "./world-ui/characters.ts";

/**
 * What a crossing's welcome asks of the rest of the session: forget what belonged to the map left (the handle-bound and
 * map-bound state, never what the gateway or the next join's frames carry over), and end the handoff with no fade.
 */
export interface CrossingMoves {
  readonly forgetMap: () => void;
  readonly landed: () => void;
}

/**
 * The page's side of seamless travel (ADR-147 decisions 13, 15, 18, 19 and 26): the other maps' figures (`EdgeFigures`),
 * fed each `edgeView`, `edgeNear` and each snapshot's `crossed` records, and the crossing's `welcome`, which moves the
 * render origin to the map reached in one step: every figure is held, the scene stands on the map it booted behind the
 * world shown, the prediction and every held figure move by the frame difference, and the streams take their figures
 * back from the hold. Nothing fades and no loading screen shows.
 */
export class SessionCrossing {
  private figures: EdgeFigures | null = null;
  private own: OwnName | null = null;
  private version = "";
  private readonly vanished: number[] = [];

  /**
   * @param parts The session's parts.
   * @param make Makes a collection for a neighbour's figures, as the owning map's is made.
   */
  public constructor(
    private readonly parts: SessionParts,
    private readonly make: () => RemoteCharacters,
  ) {
    gameReport.figuresNamed = (first) => this.named(first);
  }

  /**
   * Every figure of a first name the page draws (the page's own, the owning map's, the neighbours' and the held ones), in
   * world pixels from the world's origin, so a browser suite sees one continuous figure through a crossing whatever map
   * the page stands on. Built when asked.
   *
   * @param first The first name.
   * @returns Each figure's drawn point and where it is drawn from.
   */
  private named(first: string): { readonly x: number; readonly y: number; readonly from: string }[] {
    const { host, local, remotes, state } = this.parts;
    const world = host.world();
    const found: { x: number; y: number; from: string }[] = [];
    if (world === null) {
      return found;
    }
    const place = world.maps.find((map) => map.id === world.mapId)?.place ?? { x: 0, y: 0 };
    const ox = place.x * world.tileSize;
    const oy = place.y * world.tileSize;
    if (this.own?.first === first && local.sprite !== null) {
      found.push({ x: local.drawn.x + ox, y: local.drawn.y + oy, from: "own" });
    }
    for (const [handle, drawn] of remotes.drawn) {
      if (handle !== state.handle && remotes.stateOf(handle)?.name.first === first) {
        found.push({ x: drawn.drawnX + ox, y: drawn.drawnY + oy, from: "owner" });
      }
    }
    this.figures?.named(first, (x, y, from) => found.push({ x: x + ox, y: y + oy, from }));
    return found;
  }

  /**
   * The other maps' figures, null before a welcome.
   *
   * @returns The figures.
   */
  public get edges(): EdgeFigures | null {
    return this.figures;
  }

  /**
   * Stands the figures on the map a welcome named: they are made for the world's version the first time and again when it
   * changes, and the owning map's collection takes held figures by their key on it.
   *
   * @param world The stage the page stands on.
   * @param welcome The welcome, for the page's own name.
   */
  public stand(world: SessionWorld, welcome: Welcome): void {
    this.own = { first: welcome.firstName, family: welcome.familyName };
    if (this.figures === null || this.version !== world.version) {
      this.figures?.clear();
      this.figures = new EdgeFigures({ maps: world.maps, version: world.version, make: this.make, now: this.parts.host.now });
      this.version = world.version;
    }
    this.figures.stand(world.mapId, this.parts.remotes, world.grid);
    gameReport.edgeHeld = () => this.figures?.held ?? 0;
  }

  /**
   * Takes an `edgeNear`: the book learns whether the neighbour is near and the version it runs, and the ground beside the
   * map hides a neighbour on another version (ADR-147 decision 26).
   *
   * @param frame The frame.
   */
  public edgeNear(frame: EdgeNearFrame): void {
    const world = this.parts.host.world();
    if (world === null) {
      return;
    }
    if (this.figures === null) {
      this.figures = new EdgeFigures({ maps: world.maps, version: world.version, make: this.make, now: this.parts.host.now });
      this.version = world.version;
      this.figures.stand(world.mapId, this.parts.remotes, world.grid);
    }
    this.figures.near(frame);
    this.parts.host.maps?.showNeighbour?.(frame.map, frame.version === world.version);
  }

  /**
   * Takes one `edgeView`.
   *
   * @param view The view, valid only during the call.
   */
  public edgeView(view: Readonly<EdgeViewRecord>): void {
    if (this.parts.state.handle >= 0) {
      this.figures?.receive(view, this.own);
    }
  }

  /**
   * Reads a snapshot of the owning map before its records reach the owning collection: each `crossed` record's figure is
   * held, and so is each player a keyframe no longer carries, since a keyframe has no removal list and a player that
   * crossed while the page missed the delta leaves no `crossed` (ADR-147 note 14).
   *
   * @param record The decoded snapshot.
   * @param entities The view after it.
   */
  public snapshot(record: Readonly<SnapshotRecord>, entities: ReadonlyMap<number, unknown>): void {
    const figures = this.figures;
    if (figures === null) {
      return;
    }
    for (const handle of record.crossed) {
      figures.leftOwner(handle);
    }
    if (!record.keyframe) {
      return;
    }
    const gone = this.vanished;
    gone.length = 0;
    for (const [handle, drawn] of this.parts.remotes.drawn) {
      if (drawn.kind === EntityKind.Player && !entities.has(handle)) {
        gone.push(handle);
      }
    }
    for (const handle of gone) {
      figures.leftOwner(handle);
    }
  }

  /**
   * Takes the welcome that follows a seamless crossing's `handoffInitiated` (ADR-147 decisions 6, 18 and 19): every figure
   * is held, the view is emptied for the new map's handles, what belonged to the map left is forgotten, the scene stands
   * on the map reached (the one it booted behind the world shown) or stays on the map it holds when the welcome names it
   * (a release that put the character back), the prediction and every held figure move by the frame difference, the
   * owning collection starts at the welcome's tick, and the handoff ends with no fade. The minimap and the Location line
   * change with the stage.
   *
   * @param welcome The welcome, carrying `crossing`.
   * @param moves What the rest of the session does.
   * @returns False when the scene cannot stand on the map now, for the caller to take the welcome as a join.
   */
  public welcome(welcome: Welcome, moves: CrossingMoves): boolean {
    const { host, local, remotes, state } = this.parts;
    const maps = host.maps;
    const connection = this.parts.connection();
    const before = host.world();
    if (maps?.cross === undefined || connection === null || before === null || local.prediction === null) {
      return false;
    }
    if (before.mapId !== welcome.map && !maps.ready?.(welcome.map)) {
      return false;
    }
    this.figures?.handOver();
    emptyClientView(connection.view);
    moves.forgetMap();
    const shift = before.mapId === welcome.map ? { dx: 0, dy: 0 } : maps.cross(welcome.map);
    const world = host.world();
    if (shift === null || world === null) {
      return false;
    }
    local.cross(world.grid, shift.dx, shift.dy, world.tileSize / 16);
    this.figures?.shift(shift.dx, shift.dy);
    remotes.clear();
    remotes.begin(world.grid, welcome.tick);
    state.handle = welcome.handle;
    state.welcomed = true;
    this.stand(world, welcome);
    world.presence.reset();
    gameReport.crossings += 1;
    moves.landed();
    return true;
  }

  /**
   * Draws the other maps' figures and the held ones.
   *
   * @param delta The frame's delta, in milliseconds.
   * @param time The scene's time, in milliseconds.
   * @param scale World pixels a sixteenth.
   * @param localX Where the page's own character is drawn, across, in whole world pixels.
   * @param localY The same down.
   * @param renderedX Where it is rendered before rounding, across, in sixteenths.
   * @param renderedY The same down.
   */
  public draw(delta: number, time: number, scale: number, localX: number, localY: number, renderedX: number, renderedY: number): void {
    this.figures?.draw(delta, time, scale, localX, localY, renderedX, renderedY);
  }

  /**
   * Hands the other maps' figures and the held ones to a visitor (`EdgeFigures.visit`).
   *
   * @param visit The visitor.
   */
  public visit(visit: CharacterVisitor): void {
    this.figures?.visit(visit);
  }

  /**
   * Lets every figure go, as a session ends.
   */
  public clear(): void {
    this.figures?.clear();
    this.figures = null;
    this.own = null;
  }
}
